import { assert } from '@ember/debug';
import { action } from '@ember/object';
import { next, schedule } from '@ember/runloop';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { getChangesetErrorsForProperty } from 'donor-elf-web/lib/changeset-utils';
import ContactsCache from 'donor-elf-web/services/contacts-cache';
import validateListSelectionAddOption from 'donor-elf-web/validations/list-selection-add-option';
import Changeset from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import fuzzysort from 'fuzzysort';

export type FormListSelectionChangedArgs = {
  selectedListOptions: FormListSelectionOption[];
  allListOptions: FormListSelectionOption[]; // this will include any new options that were added
};

interface ComponentArgs {
  // it's expected that the listOptions are already sorted when passed in
  listOptions: FormListSelectionOption[];
  singleSelection?: boolean; // if true, then only one option can be selected at a time
  listHeightClass: string; // ie: 'tw-h-64' or 'tw-max-h-40'
  userCanAddOptions?: boolean; // if true, it allows the user to add options
  addOptionLabel?: string; // if blank then it's "Option"
  onSelectionsChanged: (args: FormListSelectionChangedArgs) => void;
}

export class FormListSelectionOption {
  constructor(args: {
    val: any;
    selected: boolean;
    label?: string;
    index?: number;
    valIsContactId?: boolean;
  }) {
    this.val = args.val;
    this.selected = args.selected;
    this.label = args.label;
    this.index = args.index;
    this.valIsContactId = args.valIsContactId;
  }

  @tracked selected: boolean;
  val: any;
  label?: string; // optional. if null then val is used and val must be a string
  index?: number; // this can be used to sort the options later
  valIsContactId?: boolean;

  get displayLabel() {
    return this.label || this.val;
  }
}

export default class extends Component<ComponentArgs> {
  constructor(owner: any, args: ComponentArgs) {
    super(owner, args);
    this.listOptions = [...args.listOptions];
    this.showSearch = this.listOptions.length > 3;

    assert('@listHeightClass is required', isPresent(args.listHeightClass));
    assert("@listOptions can't be null", args.listOptions != null);
    assert(
      "@userCanAddOptions can't be true when @listOptions vals are contact Ids",
      !args.userCanAddOptions || !this.listOptionsAreContactIds,
    );
  }

  @service declare contactsCache: ContactsCache;

  @tracked showSearch = true;
  @tracked searchVal: string = '';
  @tracked newOptionVal: string = '';
  @tracked newOptionChangeset?: any;
  @tracked listOptions: FormListSelectionOption[];
  optionsContainer?: HTMLElement;

  get singleSelection() {
    return this.args.singleSelection || false;
  }

  get inputType() {
    return this.singleSelection ? 'radio' : 'checkbox';
  }

  get listOptionsAreContactIds() {
    return this.listOptions.length > 0 && this.listOptions[0].valIsContactId;
  }

  get listOptionsEstimatedHeight() {
    return this.listOptionsAreContactIds ? 45 : 29;
  }

  get addOptionLabel() {
    return this.args.addOptionLabel || 'Option';
  }

  @cached
  get listOptionsByVal() {
    let listOptionsByVal: { [val: string]: FormListSelectionOption } = {};
    for (let option of this.listOptions) {
      listOptionsByVal[option.val] = option;
    }
    return listOptionsByVal;
  }

  get filteredOptions() {
    if (this.searchVal.length > 1) {
      if (this.listOptionsAreContactIds) {
        let { listOptionsByVal } = this;
        return this.contactsCache.search(this.searchVal).map((x) => {
          return listOptionsByVal[x.id];
        });
      } else {
        return fuzzysort
          .go(this.searchVal!, this.listOptions, { key: 'displayLabel' })
          .map((x) => x.obj);
      }
    } else {
      return this.listOptions;
    }
  }

  willDestroy() {
    this.optionsContainer = undefined;
  }

  @action
  listOptionsDidUpdate() {
    this.listOptions = [...this.args.listOptions];
    this.showSearch = this.listOptions.length > 3;
    this.searchVal = '';
    this.recalculateAndSetOptionsContainerHeight();
  }

  @action
  handleSearchInput(e: any) {
    this.searchVal = e.target!.value;
    schedule('afterRender', this, () => {
      this.optionsContainer!.scrollTop = 0;
    });
  }

  @action
  handleAddOptionClosed() {
    this.newOptionVal = '';
    this.newOptionChangeset = undefined;
  }

  @action
  handleNewOptionValInput(e: any) {
    this.newOptionVal = e.target.value;
    if (this.searchVal.length > 0) {
      this.searchVal = '';
    }
  }

  createNewOptionChangeset() {
    this.newOptionVal = this.newOptionVal.trim();
    let validation = validateListSelectionAddOption({
      existingOptionVals: this.listOptions.map((x) => x.displayLabel),
      addOptionsLabel: this.addOptionLabel,
    });
    this.newOptionChangeset = new Changeset(
      { val: this.newOptionVal },
      lookupValidator(validation),
      validation,
    );
  }

  get newOptionErrorMessage() {
    if (this.newOptionChangeset?.get('isInvalid')) {
      return getChangesetErrorsForProperty(
        this.newOptionChangeset,
        'val',
      )?.join(', ');
    } else {
      return undefined;
    }
  }

  recalculateAndSetOptionsContainerHeight(
    args: { scrollToTopAfterRender?: boolean } = {},
  ) {
    if (this.optionsContainer) {
      this.optionsContainer.style.width = '';
      this.optionsContainer.style.height = '';
      next(this, () => {
        if (!this.optionsContainer) {
          return;
        }
        // prevent the options container from growing or shrinking
        this.optionsContainer.style.width =
          this.optionsContainer!.getBoundingClientRect().width + 'px';
        let currentHeight =
          this.optionsContainer.getBoundingClientRect().height;
        if (currentHeight > 10) {
          this.optionsContainer.style.height = `${currentHeight}px`;
          if (args.scrollToTopAfterRender) {
            next(this, () => {
              if (this.optionsContainer) {
                this.optionsContainer.scrollTop =
                  this.optionsContainer.scrollHeight;
              }
            });
          }
        }
        if (
          this.optionsContainer.querySelector('.occluded-content:last-child')
            ?.style?.height === '0px'
        ) {
          // don't show the search if there aren't many options
          this.showSearch = false;
        }
      });
    }
  }

  @action
  optionsContainerDidInsert(e: HTMLElement) {
    this.optionsContainer = e;
    this.recalculateAndSetOptionsContainerHeight();
  }

  @action
  handleSaveNewOption() {
    this.saveNewOption();
  }

  @action
  handleSaveNewOptionAndClose(dropdown: any) {
    this.saveNewOption(dropdown);
  }

  // if dropdown is passed in, then it will close the dropdown after saving
  async saveNewOption(dropdown?: any) {
    this.createNewOptionChangeset();
    await this.newOptionChangeset.validate();
    if (this.newOptionChangeset.get('isValid')) {
      if (this.singleSelection) {
        for (let option of this.listOptions) {
          if (option.selected) {
            option.selected = false;
          }
        }
      }
      this.listOptions = [
        ...this.listOptions,
        new FormListSelectionOption({ val: this.newOptionVal, selected: true }),
      ];
      this.args.onSelectionsChanged({
        selectedListOptions: this.selectedListOptions,
        allListOptions: this.listOptions,
      });
      this.newOptionVal = '';
      dropdown?.actions.close();
      this.recalculateAndSetOptionsContainerHeight({
        scrollToTopAfterRender: true,
      });
    }
  }

  get selectedListOptions() {
    return this.listOptions.filter((x) => x.selected);
  }

  @action
  toggleSelected(option: FormListSelectionOption) {
    option.selected = !option.selected;
    if (this.singleSelection) {
      for (let otherOption of this.listOptions) {
        if (otherOption !== option && otherOption.selected) {
          otherOption.selected = false;
        }
      }
    }
    this.args.onSelectionsChanged({
      selectedListOptions: this.selectedListOptions,
      allListOptions: this.listOptions,
    });
  }
}
